import { useState } from 'react';
import SafeIcon from './SafeIcon';
import './setup.css';
import { useApp } from '../i18n/AppContext';
import { useUi } from '../store/ui';
import { postStatusHoldsDailySlot, useWorkspaceData } from '../store/DemoStore';
import { DEMO_TODAY } from '../store/seed';
import { MODEL_PRICES_PER_MILLION_TOKENS, usageRows } from '../store/setup';
import type { Post } from '../types';
import { aiProviderName } from './aiProviders';

type ExtraFormatSlots = { n: number; formats: Post['format'][] };
const BASE_POSTS_PER_CHANNEL_PER_DAY = 1;
const extraFormatSlotsConfiguredByHakusana: Partial<Record<Post['channel'], ExtraFormatSlots>> = {};
const fallbackExampleUsdPricePerMillionTokens = { in: 0.4, out: 1.6 };
const TOKENS_PER_MILLION = 1e6;
const VISIBLE_DAY_COUNT = 14;
const DAYS_IN_NOVEMBER = 30;
const COLLAPSED_USAGE_ROW_COUNT = 12;

type DayDotState = 'used' | 'reserved' | 'free' | 'unused';
const dayDotStateLabel: Record<string, string> = {
  used: 'Published',
  reserved: 'Reserved',
  free: 'Available',
  unused: 'Not used',
};

export default function AllowancePage() {
  const { translate, language } = useApp();
  const { route, navigate } = useUi();
  const { workspace, posts, accounts, aiConnection, dailyLimitOverrides } = useWorkspaceData(
    route.workspaceId
  );
  const [day, setDay] = useState(DEMO_TODAY);
  const [range, setRange] = useState<'month' | 'week'>('month');
  const [showAllUsageRows, setShowAllUsageRows] = useState(false);
  const daysSinceMonday = (new Date(2026, 10, DEMO_TODAY).getDay() + 6) % 7;
  const weekStart = DEMO_TODAY - daysSinceMonday;
  const visibleDays = Array.from({ length: VISIBLE_DAY_COUNT }, (_, offset) => weekStart + offset);
  const dateFormatter = (options: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(language, options);
  const formatShortDate = (dayOfMonth: number) =>
    dateFormatter({ day: 'numeric', month: 'short' }).format(new Date(2026, 10, dayOfMonth));
  const usdFormatter = new Intl.NumberFormat(language, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  });
  const numberFormatter = new Intl.NumberFormat(language);

  const isReallyPublished = (post: Post) =>
    post.status === 'Published' &&
    !(post.results?.length && post.results.every((result) => !result.succeeded));
  function slotStateForDay(channel: Post['channel'], dayOfMonth: number) {
    const postsHoldingDay = posts.filter(
      (post) =>
        post.channel === channel && post.day === dayOfMonth && postStatusHoldsDailySlot(post.status)
    );
    const used = postsHoldingDay.filter(isReallyPublished);
    const reserved = postsHoldingDay.filter((post) => !isReallyPublished(post));
    const extraSlotCount = extraFormatSlotsConfiguredByHakusana[channel]?.n ?? 0;
    const cap = dailyLimitOverrides[channel] ?? BASE_POSTS_PER_CHANNEL_PER_DAY + extraSlotCount;
    const past = dayOfMonth < DEMO_TODAY;
    const free = past ? 0 : Math.max(0, cap - used.length - reserved.length);
    return { used, reserved, cap, free, past };
  }
  const dayDotState = (channel: Post['channel'], dayOfMonth: number): DayDotState => {
    const slotState = slotStateForDay(channel, dayOfMonth);
    if (slotState.used.length) return 'used';
    if (slotState.reserved.length) return 'reserved';
    if (slotState.past) return 'unused';
    return 'free';
  };
  const novemberDays = Array.from({ length: DAYS_IN_NOVEMBER }, (_, offset) => offset + 1);

  const usagePeriodStartDay = range === 'month' ? 1 : DEMO_TODAY - 6;
  const usageInPeriodWithModelUsedAtTheTime =
    aiConnection.status === 'connected'
      ? usageRows(posts, DEMO_TODAY)
          .filter((usageRow) => usageRow.day >= usagePeriodStartDay)
          .map((usageRow) => {
            const historyEntryForRow = aiConnection.history?.find((historyEntry) =>
              historyEntry.keys.includes(usageRow.key)
            );
            return {
              ...usageRow,
              model: historyEntryForRow?.model ?? aiConnection.model ?? '',
              provider: historyEntryForRow?.provider ?? aiConnection.provider,
            };
          })
      : [];
  const estimatedUsdCost = (usageRow: {
    inputTokens: number;
    outputTokens: number;
    model: string;
  }) => {
    const pricePerMillionTokens =
      MODEL_PRICES_PER_MILLION_TOKENS[usageRow.model] ?? fallbackExampleUsdPricePerMillionTokens;
    return (
      (usageRow.inputTokens * pricePerMillionTokens.inputPricePerMillionTokens +
        usageRow.outputTokens * pricePerMillionTokens.outputPricePerMillionTokens) /
      TOKENS_PER_MILLION
    );
  };
  const statusWord = (post: Post) =>
    post.results?.some((result) => result.uncertain) &&
    !post.results.some((result) => result.succeeded)
      ? translate('Checking result')
      : translate(post.status);
  const hasFailedAccount = (post: Post) =>
    post.results?.some((result) => !result.succeeded && !result.uncertain);
  const totalEstimatedCost = usageInPeriodWithModelUsedAtTheTime.reduce(
    (sum, usageRow) => sum + estimatedUsdCost(usageRow),
    0
  );
  const totalTokens = usageInPeriodWithModelUsedAtTheTime.reduce(
    (sum, usageRow) => sum + usageRow.inputTokens + usageRow.outputTokens,
    0
  );
  const visibleUsageRows = showAllUsageRows
    ? usageInPeriodWithModelUsedAtTheTime
    : usageInPeriodWithModelUsedAtTheTime.slice(0, COLLAPSED_USAGE_ROW_COUNT);

  return (
    <div className="content-stack allowance-page">
      <div>
        <h1>{translate('Plan allowance')}</h1>
        <p className="subtitle">
          {workspace.name} ·{' '}
          {translate(
            'Each channel can publish one post per day. Unused days do not carry over, so a quiet week never means extra posts today. Extra slots for a format, such as a Story, are set by Hakusana; none are set for this workspace.'
          )}
        </p>
      </div>
      <div className="panel">
        <div className="panel-header">
          <h2>{translate('Choose a day')}</h2>
          <span className="muted">
            {dateFormatter({ weekday: 'long', day: 'numeric', month: 'long' }).format(
              new Date(2026, 10, day)
            )}
          </span>
        </div>
        <div className="day-strip" role="group" aria-label={translate('Choose a day')}>
          {visibleDays.map((dayOfMonth) => (
            <button
              key={dayOfMonth}
              className={`day-pill ${dayOfMonth === day ? 'selected' : ''} ${dayOfMonth === DEMO_TODAY ? 'today' : ''}`}
              aria-pressed={dayOfMonth === day}
              onClick={() => setDay(dayOfMonth)}
            >
              <span>
                {dateFormatter({ weekday: 'short' })
                  .format(new Date(2026, 10, dayOfMonth))
                  .replace(/\.$/, '')}
              </span>
              <strong>{new Date(2026, 10, dayOfMonth).getDate()}</strong>
            </button>
          ))}
        </div>
      </div>
      <div className="grid-cards allowance-cards">
        {workspace.channels.map((channel) => {
          const slotState = slotStateForDay(channel, day);
          const account = accounts.find(
            (item) => item.network === channel && !/personal/i.test(item.type)
          );
          const blocked = account && account.health !== 'Connected';
          const daysWithFreeSlotCount = visibleDays.filter(
            (dayOfMonth) =>
              dayOfMonth >= DEMO_TODAY && slotStateForDay(channel, dayOfMonth).free > 0
          ).length;
          const capSourceSuffix =
            dailyLimitOverrides[channel] !== undefined
              ? ` · ${translate('set by the platform')}`
              : extraFormatSlotsConfiguredByHakusana[channel]
                ? ` · ${translate('1 + 1 Reel/Story')}`
                : '';
          const availabilityMessage = slotState.past
            ? translate('This day has passed. Unused slots do not carry over.')
            : slotState.free
              ? translate('{n} of {cap} slots available on this day', {
                  n: slotState.free,
                  cap: slotState.cap,
                })
              : translate('No slot left on this day');
          return (
            <div className="panel allowance-card" key={channel}>
              <div className="panel-header">
                <h2>{channel}</h2>
                <span className="muted small-print">
                  {translate('{n} per day', { n: slotState.cap })}
                  {capSourceSuffix}
                </span>
              </div>
              <dl className="allowance-counts">
                <div>
                  <dt>{translate('Published')}</dt>
                  <dd>{slotState.used.length}</dd>
                </div>
                <div>
                  <dt>{translate('Reserved')}</dt>
                  <dd>{slotState.reserved.length}</dd>
                </div>
                <div>
                  <dt>{translate('Available')}</dt>
                  <dd>{slotState.past ? '—' : slotState.free}</dd>
                </div>
              </dl>
              {[...slotState.used, ...slotState.reserved].map((post) => (
                <button
                  key={post.id}
                  className="link-button small-print allowance-post"
                  onClick={() => navigate({ page: 'post', id: post.id })}
                >
                  {post.time} · {post.title} · {statusWord(post)}
                  {hasFailedAccount(post)
                    ? ` · ${translate('not every account published; still reserved until retried or cancelled')}`
                    : ''}
                </button>
              ))}
              <p className="muted small-print">{availabilityMessage}</p>
              <div className="slot-row" aria-label={translate('Next two weeks')}>
                {visibleDays.map((dayOfMonth) => (
                  <span
                    key={dayOfMonth}
                    className={`slot-dot ${dayDotState(channel, dayOfMonth)} ${dayOfMonth === day ? 'current' : ''}`}
                    title={`${formatShortDate(dayOfMonth)} · ${translate(dayDotStateLabel[dayDotState(channel, dayOfMonth)])}`}
                  />
                ))}
              </div>
              <span className="muted small-print">
                {translate('{n} days with a free slot in the next two weeks', {
                  n: daysWithFreeSlotCount,
                })}
              </span>
              {blocked && (
                <button
                  className="button small"
                  onClick={() => navigate({ page: 'settings', id: 'connections' })}
                >
                  <SafeIcon name="FiAlertTriangle" size={13} />
                  {translate('Reconnect {network} to use these slots', { network: channel })}
                </button>
              )}
            </div>
          );
        })}
      </div>
      <div className="slot-legend muted">
        {(['used', 'reserved', 'free', 'unused'] as const).map((key) => (
          <span key={key}>
            <span className={`slot-dot ${key}`} /> {translate(dayDotStateLabel[key])}
          </span>
        ))}
      </div>

      <div className="panel">
        <div className="panel-header">
          <div>
            <h2>{translate('November at a glance')}</h2>
            <p className="muted small-print">
              {translate(
                'Dates, not a balance: each date can be used once per channel, and only on that date.'
              )}
            </p>
          </div>
        </div>
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>{translate('Channel')}</th>
                <th>{translate('Published days')}</th>
                <th>{translate('Past days awaiting a result')}</th>
                <th>{translate('Past days not used')}</th>
                <th>{translate('Reserved from today')}</th>
                <th>{translate('Free from today')}</th>
                <th>{translate('Dates')}</th>
              </tr>
            </thead>
            <tbody>
              {workspace.channels.map((channel) => {
                const novemberDayStates = novemberDays.map((dayOfMonth) => ({
                  dayOfMonth,
                  state: dayDotState(channel, dayOfMonth),
                }));
                type NovemberDayState = (typeof novemberDayStates)[number];
                const countDays = (matches: (dayState: NovemberDayState) => boolean) =>
                  novemberDayStates.filter(matches).length;
                return (
                  <tr key={channel}>
                    <td>
                      <strong>{channel}</strong>
                    </td>
                    <td>{countDays((dayState) => dayState.state === 'used')}</td>
                    <td>
                      {countDays(
                        (dayState) =>
                          dayState.state === 'reserved' && dayState.dayOfMonth < DEMO_TODAY
                      )}
                    </td>
                    <td>{countDays((dayState) => dayState.state === 'unused')}</td>
                    <td>
                      {countDays(
                        (dayState) =>
                          dayState.state === 'reserved' && dayState.dayOfMonth >= DEMO_TODAY
                      )}
                    </td>
                    <td>{countDays((dayState) => dayState.state === 'free')}</td>
                    <td>{novemberDayStates.length}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="panel" id="ai-usage">
        <div className="panel-header">
          <div>
            <h2>{translate('AI usage')}</h2>
            <p className="muted small-print">
              {translate('Billed by your AI provider, separately from your AMY plan')}
            </p>
          </div>
          <select
            className="select-field"
            aria-label={translate('Period')}
            value={range}
            onChange={(event) => setRange(event.target.value as 'month' | 'week')}
          >
            <option value="month">{translate('1–{d} November', { d: DEMO_TODAY })}</option>
            <option value="week">{translate('Last 7 days')}</option>
          </select>
        </div>
        {aiConnection.status !== 'connected' ? (
          <div className="notice-banner">
            <SafeIcon name="FiInfo" />
            <span>
              {translate(
                aiConnection.trial
                  ? 'No AI account of yours is connected yet. AMY drafts on Hakusana’s trial allowance, which is not billed to you and not listed here. Your usage appears after you connect AI.'
                  : 'No AI account is connected, so AMY is not drafting and there is no usage to show.'
              )}
            </span>
            <button
              className="button small"
              onClick={() => navigate({ page: 'onboarding', id: 'ai' })}
            >
              {translate('Connect AI')}
            </button>
          </div>
        ) : (
          <>
            <div className="summary-row">
              <div className="summary-card">
                <strong>{aiProviderName(aiConnection.provider)}</strong>
                <span>
                  {translate('Current model: {model}', {
                    model: translate(aiConnection.model ?? ''),
                  })}
                </span>
              </div>
              <div className="summary-card">
                <strong>{usageInPeriodWithModelUsedAtTheTime.length}</strong>
                <span>{translate('AI tasks in this period (drafts and revisions)')}</span>
              </div>
              <div className="summary-card">
                <strong>{numberFormatter.format(totalTokens)}</strong>
                <span>{translate('Tokens in this period')}</span>
              </div>
              <div className="summary-card">
                <strong>{usdFormatter.format(totalEstimatedCost)}</strong>
                <span>{translate('Estimated cost in this period (US$)')}</span>
              </div>
            </div>
            <div className="table-wrap">
              <table className="data-table usage-table">
                <thead>
                  <tr>
                    <th>{translate('Date')}</th>
                    <th>{translate('Task')}</th>
                    <th>{translate('Model')}</th>
                    <th>{translate('Tokens in / out')}</th>
                    <th>{translate('Estimated cost')}</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleUsageRows.map((usageRow) => (
                    <tr key={usageRow.key}>
                      <td>{formatShortDate(usageRow.day)}</td>
                      <td>
                        {translate(usageRow.task)}: {usageRow.title}
                      </td>
                      <td>
                        {translate(usageRow.model)}
                        {usageRow.provider !== aiConnection.provider
                          ? ` · ${aiProviderName(usageRow.provider)}`
                          : ''}
                      </td>
                      <td>
                        {numberFormatter.format(usageRow.inputTokens)} /{' '}
                        {numberFormatter.format(usageRow.outputTokens)}
                      </td>
                      <td>{usdFormatter.format(estimatedUsdCost(usageRow))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {usageInPeriodWithModelUsedAtTheTime.length > COLLAPSED_USAGE_ROW_COUNT && (
              <button
                className="link-button"
                onClick={() => setShowAllUsageRows(!showAllUsageRows)}
              >
                {showAllUsageRows
                  ? translate('Show fewer')
                  : translate('Show all {n} tasks', {
                      n: usageInPeriodWithModelUsedAtTheTime.length,
                    })}
              </button>
            )}
            <p className="muted small-print">
              {translate(
                'How this is estimated: each task’s tokens times the example list price of the model it used, per million tokens in / out: small US$ 0.10 / 0.40, mid-size US$ 0.40 / 1.60, large US$ 2 / 8 (checked 3 Nov 2026). Providers bill in US dollars; your provider’s invoice is the real amount.'
              )}
            </p>
            <p className="muted small-print">
              {translate(
                'Image and video rendering and storage are part of your AMY plan and are not on this list or on your AI bill.'
              )}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
